import React, { useEffect, useRef } from 'react';
import './CreateOptionsMenu.css';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shared CreateOptionsMenu Component
 * Issue #101: Updated to navigate to dedicated create pages instead of popovers/modals
 * Issue #115: Added keyboard accessibility
 *
 * @param {Boolean} isOpen - Whether the menu is open
 * @param {Function} onClose - Close the menu
 * @param {Function} onCreatePost - Create new post callback (optional, uses context if not provided)
 * @param {Function} onCreateMarketplace - Create marketplace object callback (optional, uses context if not provided)
 */
const CreateOptionsMenu = ({
  isOpen,
  onClose,
  onCreatePost,
  onCreateMarketplace
}) => {
  const navigation = useNavigation();
  const { t } = useLanguage();
  const menuRef = useRef(null);
  const firstItemRef = useRef(null);

  // Issue #101: Navigate to dedicated pages instead of inline/modal
  const handleCreatePost = onCreatePost || navigation.onNavigateToCreatePost;
  const handleCreateMarketplace = onCreateMarketplace || navigation.onNavigateToCreateMarketplace;

  // Issue #115: Focus first menu item when menu opens
  // Issue #262: Add delay to prevent keyboard flash on mobile touch devices
  useEffect(() => {
    if (isOpen && firstItemRef.current) {
      // Small delay allows touch events to fully resolve before focusing
      // This prevents the keyboard from briefly appearing on mobile
      const timeoutId = setTimeout(() => {
        firstItemRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  // Issue #115: Keyboard navigation handler for menu items
  const handleMenuItemKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = e.currentTarget.nextElementSibling;
      if (next?.getAttribute('role') === 'menuitem') next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = e.currentTarget.previousElementSibling;
      if (prev?.getAttribute('role') === 'menuitem') prev.focus();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="create-options-overlay" onClick={onClose}></div>
      <div className="create-options-menu" role="menu" aria-label={t('createOptions.menu') || 'Erstellen'} ref={menuRef}>
        <button
          ref={firstItemRef}
          className="create-option"
          role="menuitem"
          tabIndex={0}
          onClick={() => {
            onClose();
            handleCreatePost?.();
          }}
          onKeyDown={handleMenuItemKeyDown}
        >
          <span className="option-icon" aria-hidden="true">&#128221;</span>
          <span className="option-text">{t('createOptions.newPost')}</span>
        </button>
        <button
          className="create-option"
          role="menuitem"
          tabIndex={0}
          onClick={() => {
            onClose();
            handleCreateMarketplace?.();
          }}
          onKeyDown={handleMenuItemKeyDown}
        >
          <span className="option-icon" aria-hidden="true">&#128722;</span>
          <span className="option-text">{t('createOptions.newMarketplace')}</span>
        </button>
      </div>
    </>
  );
};

export default CreateOptionsMenu;
